import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { type AdventureMap, getUserMaps } from "../lib/api";
import { routes } from "../lib/routes";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { MapList } from "../components/builder";

/**
 * Maps list view - shows all user's adventure maps.
 * Rendered as an overlay on the shared map (header elements in parent BuilderLayout).
 */
export default function MapsList() {
  const [maps, setMaps] = useState<AdventureMap[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadMaps();
  }, []);

  const loadMaps = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getUserMaps();
      setMaps(data);
    } catch {
      setError("Failed to load your maps. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open modal={false}>
      <DialogContent
        showCloseButton={false}
        hideBackdrop
        className="z-floating-panel flex flex-col gap-0 p-0 overflow-hidden md:max-w-4xl"
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="shrink-0 border-b border-border px-6 py-4 md:px-8 md:py-6">
          <div className="flex justify-between items-center gap-4">
            <div className="flex flex-col gap-1 text-left">
              <DialogTitle className="text-lg md:text-2xl md:font-semibold">Your Adventure Maps</DialogTitle>
              <p className="text-xs text-muted-foreground md:text-sm">
                <span className="md:hidden">
                  {maps.length} {maps.length === 1 ? "map" : "maps"}
                </span>
                <span className="hidden md:inline">Create and manage your adventure maps</span>
              </p>
            </div>
            <Button asChild size="lg" className="whitespace-nowrap max-md:size-default">
              <Link to={routes.to.maps.new()}>
                <span className="md:hidden">+ New</span>
                <span className="hidden md:inline">+ New Map</span>
              </Link>
            </Button>
          </div>
        </DialogHeader>

        <div className="min-h-0 grow overflow-y-auto px-6 py-4 md:px-8 md:py-6">
          <MapList maps={maps} loading={loading} error={error} onMapsChange={setMaps} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
